import AssetEnvLight from "./asset-env-light";

// setting this to true causes a bug where some textures do not load
const SKIP_ALREADY_ADDED_ITEMS = false;

const AssetLibContainers = {
    textures: {},
    geometries: {},
    lights: {},
    materials: {},
    objects: {},
    animations: {},
};

const AssetLibrary = new class AssetLibrary {
    constructor() {
        this.events = new THREE.EventDispatcher();

        for (let assetType in AssetLibContainers) {
            let proxy = new Proxy(AssetLibContainers[assetType], {
                set: (container, uuid, asset, receiver) => {
                    let oldAsset = container[uuid];
                    container[uuid] = asset;

                    if (oldAsset) {
                        this.events.dispatchEvent({
                            type: 'remove',
                            assetType,
                            uuid,
                            asset: oldAsset,
                        });
                    }

                    this.events.dispatchEvent({
                        type: 'add',
                        assetType,
                        uuid,
                        asset,
                    });

                    return true;
                },

                deleteProperty: (container, uuid, receiver) => {
                    let oldAsset = container[uuid];
                    delete container[uuid];

                    if (oldAsset) {
                        this.events.dispatchEvent({
                            type: 'remove',
                            assetType,
                            uuid,
                            asset: oldAsset,
                        });
                    }

                    return true;
                },
            });

            Object.defineProperty(this, assetType, {
                configurable: false,
                enumerable: true,
                value: proxy,
                writable: false,
            });
        }

        this.envLight = new AssetEnvLight();

        /**
         * @type {AssetQueue[]}
         */
        this.assetQueues = [];
        /**
         * @type {AssetQueue[]}
         */
        this.assetQueuesLOD = [];
        this.assetMap = {};
        this.references = {};
    }

    clear() {
        ["materials", "geometries", "textures"]
            .map(key => Object.values(this[key]))
            .flat()
            .forEach(obj => {
                if (obj && obj.dispose) obj.dispose();
            });

        Object.keys(AssetLibContainers)
            .forEach(type => {
                Object.keys(AssetLibContainers[type])
                    .forEach(key => {
                        delete AssetLibContainers[type][key];
                    });
            });

        this.envLight = new AssetEnvLight();

        /**
         * @type {AssetQueue[]}
         */
        this.assetQueues = [];
        /**
         * @type {AssetQueue[]}
         */
        this.assetQueuesLOD = [];
        this.assetMap = {};
        this.references = {};
    }
}

class AssetQueue {
    /**
     * Constructor.
     * @param {number} slideIndex Slide index to which the queue belongs to.
     */
    constructor(slideIndex = -1) {
        this.geometries = new Queue();
        this.textures = new Queue();
        this.highPriority = new Queue(true);
        this.livelabels = new Queue();
        this.envlight = new Queue();
        this.animations = new Queue();
        this.slideIndex = slideIndex;

        Object.freeze(this);
    }

    /**
     * @returns {number}
     */
    calculateSize() {
        return [
            "geometries",
            "textures",
            "highPriority",
            "livelabels",
            "envlight",
            "animations"
        ].reduce((accum, queueName) => {
            return accum + this[queueName].size;
        }, 0);
    }

    /**
     * Sorts the active queue.
     */
    sort() {
        Object.keys(this).forEach(q => {
            if (this[q] instanceof Queue) this[q].sort();
        });
    }

    /**
     * Gets items from asset queue.
     * @param {number} count Number of items to retrieve.
     * @return {QueueItem[]}
     */
    getItems(count) {
        const result = [];

        for (let i = 0; i < count; i++) {
            const item = this.getItem();

            if (!!item) { result.push(item); }
            else { return result; }
        }

        return result;
    }

    /**
     * Gets item from asset queue.
     * @return {QueueItem}
     */
    getItem() {

        const queue = Object.keys(this)
            .map(key => this[key])
            .reduce((prev, curr) => {
                if (!(curr instanceof Queue) || curr.queue.length === 0) {
                    return prev;
                }
                if (!prev.queue || prev.queue.length === 0) {
                    return curr;
                }
                return prev.queue[0].priority < curr.queue[0].priority ? prev : curr;
            }, {
                queue: [],
                //priority: Infinity
            }
            );


        return queue && queue.queue.shift() || null;
    }
}

export default AssetLibrary;
export { AssetLibrary, AssetQueue };

/**
 * Generic queue.
 */
class Queue {
    /**
     * Constructor.
     * @param {boolean} isHighPriority Is the queue a high priority one.
     */
    constructor(isHighPriority = false) {
        /**
         * @type {QueueItem[]}
         */
        this.queue = [];
        if (SKIP_ALREADY_ADDED_ITEMS) {
            this.items = {};
        }

        this.isHighPriority = isHighPriority;

        Object.freeze(this);
    }

    get size() { return this.queue.length; }

    /**
     * Checks if queue is empty.
     * @return {boolean}
     */
    isEmpty() { return this.size === 0; }

    /**
     * Adds an item to the asset queue.
     * @param {number} priority Asset priority when loading.
     * @param {object} data Data to add to queue.
     * @param {Object} loaders Loaders.
     */
    addItem(priority, data, loaders) {
        const wrappedLoader = function (...args) {
            return new Promise((resolve, reject) => {
                const deferredCallback = true || Q3.editMode
                    ? function (handler) { handler(); }
                    : global.requestIdleCallback || function (handler) {
                        let startTime = Date.now();

                        return setTimeout(function () {
                            handler({
                                didTimeout: false,
                                timeRemaining: function () {
                                    return Math.max(0, 50.0 - (Date.now() - startTime));
                                }
                            });
                        }, 1);
                    };

                deferredCallback(
                    async function () {
                        try {
                            resolve(await loaders(...args));
                        } catch (e) { reject(e); }
                    }
                );
            });
        }

        if (SKIP_ALREADY_ADDED_ITEMS) {
            const keyItem = `${data.type}_${data.uuid}`;

            if (keyItem in this.items) return;

            this.items[keyItem] = data;
        }

        this.queue.push(new QueueItem(priority, data, wrappedLoader));
    }

    /**
     * Sorts the queue by priority.
     */
    sort() { this.queue.sort((a, b) => (a.priority - b.priority)); }
}

/**
 * Queue item.
 */
class QueueItem {
    /**
     * Constructor
     * @param {number} priority Asset priority.
     * @param {Object} data Asset data.
     * @param {Object} load
     */
    constructor(priority, data = {}, load) {
        this.priority = priority;
        this.data = data;
        this.load = load;

        Object.freeze(this);
    }
}